////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Careers listing route — static culture copy + GET /public/careers //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { Link } from 'react-router';

import { useApi } from '@/hooks/useApi';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { CareerListResponse } from '@/types/public';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { EmptyState } from '@/components/feedback/EmptyState';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal, RevealGroup, RevealItem } from '@/components/content/Reveal';

const REQUEST_URL = '/public/careers?limit=24&page=1';

export function Careers () {
  usePageTitle('Careers');

  const { status, data } = useApi<CareerListResponse>(REQUEST_URL);

  return (
    <div>
      <PageHero title="Careers" />

      <div className="mx-auto max-w-6xl px-6 py-16">
        <Reveal as="section">
          <GlassTile className="p-8 sm:p-12">
            {/*
              MARK — discovery §2.5 found `/careers/` on the live site to be a static
              culture/pitch page, but its copy was NOT captured in the scrape (no job
              listing behaviour existed to capture, and the culture copy itself was out of
              scope for 1.0.0 — see content/scrape/_structure/careers.md). Inventing that
              copy would put fabricated marketing claims on a public page, which the
              organisation's confidentiality/no-fabrication rules and 1.0.2.md §5.5's "no
              placeholder text anywhere a visitor can see" both push against. The paragraph
              below is limited to facts already established in this project's own
              governance docs (root CLAUDE.md's IRONSIDE description) — flagged here for a
              marketing/HR-approved replacement before this ships (LEDGER.md open
              decision #8).
            */}
            <p className="max-w-2xl text-lg text-muted">
              IRONSIDE is a high-volume, high-density residential builder delivering projects
              end to end — including our own in-house precast concrete yard. Vacancies, when
              open, are listed below.
            </p>

            {status === 'loading' && <div className="mt-10"><Spinner label="Loading vacancies" /></div>}

            {status === 'error' && (
              <div className="mt-10">
                <ErrorState message="Vacancies couldn't be loaded right now." />
              </div>
            )}

            {status === 'success' && data.items.length === 0 && (
              <div className="mt-10">
                <EmptyState message="There are no open vacancies at this time." />
              </div>
            )}

            {status === 'success' && data.items.length > 0 && (
              <RevealGroup as="ul" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                {data.items.map((vacancy) => (
                  <RevealItem key={vacancy.slug} as="li">
                    {/* border-muted/40 (not border-line): these cards sit on a glass
                        tile, where border-line all but vanishes in light mode
                        (Mike, 14/08/2026); muted flips with the theme. JobPosting
                        JSON-LD lives on the detail route now, not this listing. */}
                    <Link
                      to={`/careers/${vacancy.slug}`}
                      className="block rounded border border-muted/40 p-4 transition-colors hover:border-muted focus:ring-2 focus:ring-ink/40 focus:outline-none"
                    >
                      <p className="font-medium text-ink">{vacancy.title}</p>
                      <table className="mt-2 text-sm">
                        <tbody>
                          {vacancy.discipline && (
                            <tr>
                              <th scope="row" className="pr-4 text-left font-medium text-muted">Division:</th>
                              <td className="text-ink">{vacancy.discipline}</td>
                            </tr>
                          )}
                          {vacancy.location && (
                            <tr>
                              <th scope="row" className="pr-4 text-left font-medium text-muted">Location:</th>
                              <td className="text-ink">{vacancy.location}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </Link>
                  </RevealItem>
                ))}
              </RevealGroup>
            )}
          </GlassTile>
        </Reveal>
      </div>
    </div>
  );
}
